import { TextInput } from '@core/components/Inputs';
import { Grid } from '@material-ui/core';
import SelectMunicipioV2 from 'app/components/Select/SelectMunicipioV2';
import SelectPais from 'app/components/Select/SelectPais';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { InputCep } from '../../../components/Input';
import { SelectUnidadeFederativa } from '../../../components/Select';
import CampoComEspacoIconeAjuda from 'app/components/CampoComEspacoIconeAjuda';

const MAX_LENGTHS_PADRAO = {
  logradouro: 100,
  numero: 50,
  complemento: 50,
  bairro: 100,
  referencia: 100,
};

const EnderecoForm = ({ readOnly, basePath = 'pessoa.endereco', maxLengths, reservarEspacoIconeAjuda = false, autoFocusCep = true }) => {
  const { values, handleChange, handleBlur, errors, touched } = useFormikContext();

  const limites = { ...MAX_LENGTHS_PADRAO, ...maxLengths };

  const { pessoa = {} } = values;

  const endereco = get(values, basePath) || {};

  const { pais, cep, logradouro, numero, complemento, uf, cidade = {}, bairro, referencia } = endereco;

  const [primeiraAbertura, setPrimeiraAbertura] = useState(null);

  const erroDoCampo = (campo) => get(errors, `${basePath}.${campo}`);
  
  const touchedDoCampo = (campo) => get(touched, `${basePath}.${campo}`);

  const ufSigla = uf?.sigla;
  useEffect(() => {
    localStorage.setItem('UF', ufSigla);

    if (primeiraAbertura !== null && endereco?.cidade && (!endereco?.cidade?.uf || endereco?.cidade?.uf !== ufSigla))
      setPrimeiraAbertura('não nulo');
    // Efeito reativo apenas ao gatilho acima; incluir as demais reiniciaria o formulário durante a edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufSigla]);

  const alinharCampo = (campo) => {
    if (!reservarEspacoIconeAjuda) {
      return campo;
    }

    return <CampoComEspacoIconeAjuda>{campo}</CampoComEspacoIconeAjuda>;
  };

  return (
    <>
      {pessoa.tipoPessoa === 'ESTRANGEIRA' && (
        <Grid item xs={12}>
          <SelectPais
            id={`${basePath}.pais`}
            name={`${basePath}.pais`}
            label="País"
            onChange={handleChange}
            onBlur={handleBlur}
            value={pais}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        {alinharCampo(
          <InputCep
            readOnly={readOnly}
            disabled={pessoa.tipoPessoa === 'ESTRANGEIRA'}
            autoFocus={autoFocusCep}
            label="CEP"
            id={`${basePath}.cep`}
            idObject={basePath}
            endereco={endereco}
            handleChange={handleChange}
            onBlur={handleBlur}
            value={cep}
            touched={!!touchedDoCampo('cep')}
            error={erroDoCampo('cep')}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        {alinharCampo(
          <TextInput
            readOnly={readOnly}
            fullWidth
            label="Logradouro"
            id={`${basePath}.logradouro`}
            onChange={handleChange}
            onBlur={handleBlur}
            value={logradouro}
            maxLength={limites.logradouro}
            touched={!!touchedDoCampo('logradouro')}
            error={erroDoCampo('logradouro')}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        <TextInput
          readOnly={readOnly}
          fullWidth
          label="Número"
          id={`${basePath}.numero`}
          onChange={handleChange}
          onBlur={handleBlur}
          value={numero}
          maxLength={limites.numero}
          touched={!!touchedDoCampo('numero')}
          error={erroDoCampo('numero')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        {alinharCampo(
          <TextInput
            readOnly={readOnly}
            fullWidth
            label="Complemento"
            id={`${basePath}.complemento`}
            onChange={handleChange}
            value={complemento}
            maxLength={limites.complemento}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        <SelectUnidadeFederativa
          readOnly={readOnly}
          disabled={pessoa.tipoPessoa === 'ESTRANGEIRA'}
          fullWidth
          variant="outlined"
          label="UF"
          id={`${basePath}.uf`}
          idObject={`${basePath}.uf`}
          value={uf && (uf.id || uf.nome || uf)}
          handleChange={(event) => {
            const novaUf = event.target.value?.sigla || event.target.value;
            const ufAtual = uf?.sigla || uf;

            if (novaUf !== ufAtual && cidade?.id) {
              handleChange({
                target: {
                  id: `${basePath}.cidade`,
                  value: undefined,
                },
              });
            }
            handleChange(event);
          }}
          onBlur={handleBlur}
          touched={!!touchedDoCampo('uf')}
          error={erroDoCampo('uf')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        {alinharCampo(
          <SelectMunicipioV2
            id={`${basePath}.cidade`}
            name={`${basePath}.cidade`}
            label="Cidade"
            onChange={handleChange}
            onBlur={handleBlur}
            value={cidade}
            ufSigla={uf}
            disabled={pessoa.tipoPessoa === 'ESTRANGEIRA'}
            touched={!!touchedDoCampo('cidade')}
            error={erroDoCampo('cidade')}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        <TextInput
          readOnly={readOnly}
          fullWidth
          label="Bairro"
          id={`${basePath}.bairro`}
          value={bairro}
          maxLength={limites.bairro}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={!!touchedDoCampo('bairro')}
          error={erroDoCampo('bairro')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
        {alinharCampo(
          <TextInput
            readOnly={readOnly}
            fullWidth
            label="Referência"
            id={`${basePath}.referencia`}
            value={referencia}
            maxLength={limites.referencia}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={!!touchedDoCampo('referencia')}
            error={erroDoCampo('referencia')}
          />
        )}
      </Grid>
    </>
  );
};

EnderecoForm.propTypes = {
  readOnly: PropTypes.bool,
  basePath: PropTypes.string,
  maxLengths: PropTypes.shape({
    logradouro: PropTypes.number,
    numero: PropTypes.number,
    complemento: PropTypes.number,
    bairro: PropTypes.number,
    referencia: PropTypes.number,
  }),
  reservarEspacoIconeAjuda: PropTypes.bool,
  autoFocusCep: PropTypes.bool,
};

export default EnderecoForm;
